/**
 * Geometry for a horizontally scrolling tab strip: which edges have hidden
 * tabs behind them (so the strip can fade that edge), and how far to scroll
 * so the active tab is fully in view.
 *
 * Pure numbers in, numbers out: the component measures, this decides.
 *
 * @module components/ui/tabStripScroll
 */

/** Sub-pixel scroll positions (zoom, fractional widths) are not overflow. */
const EDGE_TOLERANCE_PX = 1;

export interface TabStripEdges {
  /** Tabs are hidden before the visible start. */
  readonly start: boolean;
  /** Tabs are hidden past the visible end. */
  readonly end: boolean;
}

/**
 * Which edges of the strip hide tabs. `scrollLeft` is taken as a magnitude,
 * so a right-to-left strip (negative `scrollLeft`) reads the same way.
 */
export function tabStripEdges(input: {
  readonly scrollLeft: number;
  readonly scrollWidth: number;
  readonly clientWidth: number;
}): TabStripEdges {
  const overflow = input.scrollWidth - input.clientWidth;
  if (overflow <= EDGE_TOLERANCE_PX) return { start: false, end: false };
  const offset = Math.min(Math.abs(input.scrollLeft), overflow);
  return {
    start: offset > EDGE_TOLERANCE_PX,
    end: overflow - offset > EDGE_TOLERANCE_PX,
  };
}

/**
 * The `scrollLeft` that brings a tab fully into view with `margin` pixels of
 * room beside it (room for the edge fade, so the tab is not drawn under it),
 * or `null` when it is already in view. Moves the least distance: a tab cut
 * off on the right is scrolled to sit at the right edge, not the left.
 */
export function tabStripScrollToReveal(input: {
  /** The tab's left edge in the strip's scroll coordinates. */
  readonly itemLeft: number;
  readonly itemWidth: number;
  readonly scrollLeft: number;
  readonly clientWidth: number;
  readonly scrollWidth: number;
  readonly margin: number;
}): number | null {
  const maxScroll = Math.max(0, input.scrollWidth - input.clientWidth);
  const clamp = (value: number) => Math.min(maxScroll, Math.max(0, value));
  // A tab wider than the visible strip keeps its start in view.
  const margin = Math.max(0, Math.min(input.margin, (input.clientWidth - input.itemWidth) / 2));
  const start = input.itemLeft - margin;
  const end = input.itemLeft + input.itemWidth + margin;
  const viewStart = input.scrollLeft;
  const viewEnd = input.scrollLeft + input.clientWidth;
  let target: number;
  if (start < viewStart || input.itemWidth >= input.clientWidth) target = start;
  else if (end > viewEnd) target = end - input.clientWidth;
  else return null;
  const next = clamp(target);
  return Math.abs(next - input.scrollLeft) <= EDGE_TOLERANCE_PX ? null : next;
}
